import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { boldElement } from 'components/ConfirmPopup';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { RoleAssignment } from 'modules/CreateWorkspaceModal/types';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { getRoomStateEvents } from 'utils/roomHierarchyUtils';

type CascadeState = { kind: 'loading' } | { kind: 'count'; count: number } | { kind: 'unknown' };

interface DeleteRoleCascadeMessageProps {
  roleId: string;
  name: string;
  isDefaultRole: boolean;
  assignmentCount: number;
}

const SPACE_CHILD_EVENT = 'm.space.child';

// Sub-workspace cascade lookup per Decision 27 (fire-and-forget on modal open).
// Reads from the Matrix client cache only — no network call, no awaitable
// dependency. AC-270 / AC-271: count > 0 surfaces the cascade line; count 0
// renders the standard subtitle. AC-272 / AC-273: read errors collapse to the
// `unknown` variant; modal renders immediately while the lookup resolves in
// the next tick (`useEffect`). AC-274 / AC-275: callers pass
// `isDefaultRole = true` for Owner / Member to bypass the lookup entirely.
const DeleteRoleCascadeMessage = ({ roleId, name, isDefaultRole, assignmentCount }: DeleteRoleCascadeMessageProps) => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { workspaceId } = useWorkspacesContext({ requireWorkspaceId: true });
  const [cascade, setCascade] = useState<CascadeState>(
    isDefaultRole ? { kind: 'count', count: 0 } : { kind: 'loading' }
  );

  useEffect(() => {
    if (isDefaultRole) return;

    let cancelled = false;

    try {
      const workspaceRoom = matrixClient.getRoom(workspaceId);
      if (!workspaceRoom) {
        if (!cancelled) setCascade({ kind: 'unknown' });
        return;
      }

      const spaceChildEvents = getRoomStateEvents(workspaceRoom, SPACE_CHILD_EVENT);
      // Tombstoned child events surface with empty content — filter them out
      // before the lookup so deleted sub-workspaces are not counted.
      const activeChildIds = spaceChildEvents
        .filter((event) => Object.keys(event.getContent() || {}).length > 0)
        .map((event) => event.getStateKey())
        .filter((id): id is string => Boolean(id));

      let referencingCount = 0;
      let lookupHadError = false;

      for (const childId of activeChildIds) {
        try {
          const childRoom = matrixClient.getRoom(childId);
          const stateEvent = childRoom?.currentState.getStateEvents(CustomMeedioMatrixEvents.WORKSPACE_ROLES, '');
          const content = stateEvent?.getContent<{ assignments?: RoleAssignment[] }>();
          const assignments = content?.assignments ?? [];

          if (assignments.some((assignment) => assignment.roleId === roleId)) {
            referencingCount += 1;
          }
        } catch {
          lookupHadError = true;
        }
      }

      if (cancelled) return;

      if (lookupHadError) {
        setCascade({ kind: 'unknown' });
      } else {
        setCascade({ kind: 'count', count: referencingCount });
      }
    } catch {
      if (!cancelled) setCascade({ kind: 'unknown' });
    }

    return () => {
      cancelled = true;
    };
  }, [isDefaultRole, matrixClient, roleId, workspaceId]);

  const subtitle = assignmentCount
    ? t('workspaces.remove_role_members_subtitle', { count: assignmentCount })
    : t('workspaces.no_members_assigned_to_role');

  let cascadeLine: string | null = null;
  if (!isDefaultRole) {
    if (cascade.kind === 'loading') {
      cascadeLine = t('workspaces.delete_role_subworkspace_cascade_loading');
    } else if (cascade.kind === 'count' && cascade.count > 0) {
      cascadeLine = t('workspaces.delete_role_subworkspace_cascade', { count: cascade.count });
    } else if (cascade.kind === 'unknown') {
      cascadeLine = t('workspaces.delete_role_subworkspace_cascade_unknown');
    }
  }

  return (
    <span className="gap-1 flex flex-col">
      <span>
        <Trans i18nKey="workspaces.are_you_sure_remove_role" values={{ name }} components={{ bold: boldElement }} />
      </span>
      <span className="text-size-sm text-gray-80">{subtitle}</span>
      {cascadeLine && <span className="text-size-sm text-gray-80">{cascadeLine}</span>}
    </span>
  );
};

export default DeleteRoleCascadeMessage;
